<template>
  <div
    :key="`${category}-${loggedIn}`"
    class="max-w-content-container relative mx-auto min-h-[500px]"
  >
    <div v-if="loading" class="absolute left-0 w-full lg:top-1/2">
      <TheLoader />
    </div>

    <div v-else class="flex flex-col gap-5">
      <div
        v-if="isAuthenticated && reports.length"
        class="mb-6 lg:absolute lg:right-0 lg:-mt-[62px]"
      >
        <SharedCommonButton
          v-if="showDownloadAll"
          text="Download all"
          class="!w-full whitespace-nowrap !text-sm lg:!w-auto"
          event-id="button-download-all"
        >
          <IconDownload class="z-index w-full min-w-[16px] max-w-[16px]" />
        </SharedCommonButton>
      </div>

      <div v-if="!reports.length" class="text-center text-xl">
        There are currently no reports published, please check back later
      </div>

      <div v-if="reports.length">
        <div
          class="grid grid-cols-1 items-center justify-center gap-4 md:grid-cols-2"
        >
          <div v-for="(report, index) in reports" :key="index">
            <SharedMarketVideoCard
              v-if="report?.tags[0]?.name === 'forecast-video'"
              :report="report"
              :initial-fields="initialFields"
              :product="product"
            />
            <SharedMarketReportCard
              v-else
              :report="report"
              :category="categoryReport"
              :initial-fields="initialFields"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { TReportCard } from '~/src/types/reports'
import type { TMarketSlug } from '~/src/types/markets'

import { useAuthorization } from '~/composables/useAuthorization'
import useAuthentication from '~/composables/useAuthentication'

import { FORECASTS } from '~/constants/services/constants'
import { MARKET_SLUG, MARKET_TYPE } from '~/src/constants/markets'

import TheLoader from '~/components/TheLoader.vue'
import SharedMarketReportCard from '~/components/shared/market/ReportCard.vue'
import SharedCommonButton from '~/components/shared/common/Button.vue'
import SharedMarketVideoCard from '~/components/shared/market/VideoCard.vue'

import IconDownload from '~/assets/icons/icon-download.svg'
import { logError } from '~/utils/log-error'
import {
  sortByTitleReport,
  sortedByForecastsRecycling,
} from '~/src/utils/reports/sort'
import { filterReports } from '~/src/utils/reports/filter'
import { setReportImages } from '~/src/utils/reports/image'

const { hasAuthorized } = useAuthorization()
const { isAuthenticated } = useAuthentication()

const { loggedIn } = useAuthentication()

const props = defineProps<{
  category: string
  categoryReport: string
  product: TMarketSlug
}>()

const showDownloadAll = ref(false)
const loading = ref(true)
const reports = ref<TReportCard[]>([])

const initialFields = computed(() => [props.product, FORECASTS.checkbox])

async function getData() {
  loading.value = true
  try {
    const { getReportAndModelData } = useReports()

    const marketSlug = props.product
    const marketTypes = [MARKET_TYPE.FORECASTS]
    const showTotal = true
    const data = await getReportAndModelData(
      marketSlug,
      marketTypes,
      showTotal,
      -1,
    )
    showDownloadAll.value = await hasAuthorized(props.categoryReport)

    const files = filterReports(data, 'forecast-video-intro')
    reports.value = sortByTitleReport(files.noMatch)
    if (props.product === MARKET_SLUG.RECYCLING) {
      reports.value = sortedByForecastsRecycling(reports.value)
    }
    reports.value = setReportImages(reports.value, marketSlug)
  } catch (error) {
    logError(error)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  getData()
})

useAuthentication(getData)
</script>
